import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { createRoot, Root } from "react-dom/client";
import BrazilPixReceipt from "./BrazilPixReceipt";
import { BrazilPixReceiptData } from "./types";

const waitForNextPaint = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });

const buildFileName = (data: BrazilPixReceiptData): string => {
  const id =
    data.transactionId || data.externalId || data.endToEndId || "receipt";
  return `pix-receipt-${id}.pdf`;
};

/**
 * Renders the receipt off-screen, captures it, and downloads a PDF.
 */
export const downloadBrazilPixReceiptPdf = async (
  data: BrazilPixReceiptData,
): Promise<void> => {
  const host = document.createElement("div");
  host.style.position = "fixed";
  host.style.left = "-10000px";
  host.style.top = "0";
  host.style.width = "760px";
  host.style.padding = "0";
  host.style.margin = "0";
  host.style.background = "#ffffff";
  host.style.pointerEvents = "none";
  document.body.appendChild(host);

  let root: Root | null = createRoot(host);

  try {
    root.render(<BrazilPixReceipt data={data} />);
    await waitForNextPaint();
    await new Promise((resolve) => setTimeout(resolve, 80));

    const receiptEl = host.querySelector(
      "#brazil-pix-receipt",
    ) as HTMLElement | null;

    if (!receiptEl) {
      throw new Error("Receipt failed to render");
    }

    const canvas = await html2canvas(receiptEl, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
      foreignObjectRendering: false,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "a4",
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 28;
    const maxWidth = pageWidth - margin * 2;
    const maxHeight = pageHeight - margin * 2;

    const widthRatio = maxWidth / canvas.width;
    const heightRatio = maxHeight / canvas.height;
    const ratio = Math.min(widthRatio, heightRatio, 1);

    const renderWidth = canvas.width * ratio;
    const renderHeight = canvas.height * ratio;
    const x = (pageWidth - renderWidth) / 2;
    const y = margin;

    if (renderHeight <= maxHeight) {
      pdf.addImage(imgData, "PNG", x, y, renderWidth, renderHeight);
    } else {
      const scaledWidth = maxWidth;
      const scaledHeight = (canvas.height * scaledWidth) / canvas.width;
      let heightLeft = scaledHeight;
      let position = margin;

      pdf.addImage(imgData, "PNG", margin, position, scaledWidth, scaledHeight);
      heightLeft -= maxHeight;

      while (heightLeft > 0) {
        position = margin - (scaledHeight - heightLeft);
        pdf.addPage();
        pdf.addImage(
          imgData,
          "PNG",
          margin,
          position,
          scaledWidth,
          scaledHeight,
        );
        heightLeft -= maxHeight;
      }
    }

    pdf.save(buildFileName(data));
  } finally {
    root?.unmount();
    root = null;
    host.remove();
  }
};
